import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { formatCurrency } from './currencyUtils';
import { buildBillPreviewCompanyDetails } from './dataAdapter';

const EXPORT_HEADERS = [
  'Date',
  'Month',
  'Employee Name',
  'Employee ID',
  'Designation',
  'Payment Mode',
  'Total Earnings',
  'Total Deductions',
  'Net Amount',
  'Settled from Wallet',
  'Source Employee',
];

const rowFromEntry = (entry) => {
  const settled = entry.settledFromOtherWallet;
  return [
    entry.date || '',
    entry.month || '',
    entry.employeeName || '',
    entry.employeeId || '',
    entry.post || '',
    entry.paymentMode || '',
    entry.totalEarnings || entry.total || 0,
    entry.totalDeductions || 0,
    entry.netAmount || entry.total || 0,
    settled?.amount > 0 ? settled.amount : '',
    settled?.amount > 0 ? settled.sourceEmployeeName || '' : '',
  ];
};

export function exportSalaryPaymentsCSV(salaryPayments) {
  let csvContent = EXPORT_HEADERS.join(',') + '\n';
  salaryPayments.forEach((entry) => {
    csvContent +=
      rowFromEntry(entry)
        .map((cell) => '"' + String(cell).replace(/"/g, '""') + '"')
        .join(',') + '\n';
  });
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `salary_payments_export_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function getCompanyLetterheadRows(currentCompany) {
  const c = buildBillPreviewCompanyDetails(currentCompany || {}, {});
  const companyName = c.firmName || c.name || 'Company Name';
  const gstin = c.gstin || c.gstNumber;
  const email = c.email;
  const phone = c.contactNumber || c.phone;
  const address = c.address;
  const city = c.city;
  const state = c.state;
  const pincode = c.pincode;

  const letterheadRows = [[companyName], ['']];
  const contactDetails = [];
  if (gstin) contactDetails.push(`GSTIN: ${gstin}`);
  if (email) contactDetails.push(`Email: ${email}`);
  if (phone) contactDetails.push(`Phone: ${phone}`);
  if (contactDetails.length) letterheadRows.push([contactDetails.join(' | ')]);

  const addressParts = [];
  if (address) addressParts.push(address);
  if (city && state) addressParts.push(`${city}, ${state}${pincode ? ` - ${pincode}` : ''}`);
  if (addressParts.length) letterheadRows.push([addressParts.join(', ')]);

  letterheadRows.push(
    [''],
    ['Salary Payments Report'],
    [`Generated on: ${new Date().toLocaleDateString('en-IN')}`],
    [''],
    ['']
  );
  return letterheadRows;
}

export function exportSalaryPaymentsExcel(salaryPayments, currentCompany) {
  const rows = salaryPayments.map(rowFromEntry);
  const allRows = [...getCompanyLetterheadRows(currentCompany), EXPORT_HEADERS, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(allRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Salary Payments');
  XLSX.writeFile(wb, `salary_payments_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export function exportSalaryPaymentsPDF(salaryPayments, currentCompany, companyCurrency) {
  const docPDF = new jsPDF();
  const headers = [
    ['Date', 'Month', 'Employee', 'Designation', 'Earnings', 'Deductions', 'Net Amount', 'From Wallet'],
  ];
  const rows = salaryPayments.map((entry) => {
    const settled = entry.settledFromOtherWallet;
    const walletInfo =
      settled?.amount > 0
        ? `${settled.sourceEmployeeName}: ${formatCurrency(Number(settled.amount), companyCurrency)}`
        : '-';
    return [
      entry.date || '',
      entry.month || '',
      `${entry.employeeName || ''} (${entry.employeeId || ''})`,
      entry.post || '',
      formatCurrency(Number(entry.totalEarnings || entry.total || 0), companyCurrency),
      formatCurrency(Number(entry.totalDeductions || 0), companyCurrency),
      formatCurrency(Number(entry.netAmount || entry.total || 0), companyCurrency),
      walletInfo,
    ];
  });

  const companyName =
    buildBillPreviewCompanyDetails(currentCompany || {}, {}).firmName || 'Company Name';
  const pageWidth = docPDF.internal.pageSize.width;
  docPDF.setFontSize(18);
  docPDF.setFont('helvetica', 'bold');
  const companyNameWidth = docPDF.getTextWidth(companyName);
  docPDF.text(companyName, (pageWidth - companyNameWidth) / 2, 20);
  docPDF.setFontSize(14);
  docPDF.text('Salary Payments Report', 20, 40);
  docPDF.setFontSize(9);
  docPDF.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 20, 46);

  import('jspdf-autotable')
    .then(({ default: autoTable }) => {
      autoTable(docPDF, {
        head: headers,
        body: rows,
        startY: 52,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [0, 102, 153] },
      });
      const totalEarnings = salaryPayments.reduce(
        (sum, entry) => sum + (Number(entry.totalEarnings || entry.total || 0)),
        0
      );
      const totalDeductions = salaryPayments.reduce(
        (sum, entry) => sum + (Number(entry.totalDeductions || 0)),
        0
      );
      const totalNet = salaryPayments.reduce(
        (sum, entry) => sum + (Number(entry.netAmount || entry.total || 0)),
        0
      );
      const finalY = docPDF.lastAutoTable?.finalY || 200;
      docPDF.setFontSize(10);
      docPDF.text(`Total Earnings: ${formatCurrency(totalEarnings, companyCurrency)}`, 20, finalY + 10);
      docPDF.text(`Total Deductions: ${formatCurrency(totalDeductions, companyCurrency)}`, 20, finalY + 20);
      docPDF.text(`Total Net: ${formatCurrency(totalNet, companyCurrency)}`, 20, finalY + 30);
      docPDF.save(`salary_payments_export_${new Date().toISOString().slice(0, 10)}.pdf`);
    })
    .catch((e) => {
      console.warn('jspdf-autotable not available:', e);
      docPDF.save(`salary_payments_export_${new Date().toISOString().slice(0, 10)}.pdf`);
    });
}
